'use client'

import { usePathname, useRouter } from 'next/navigation'
import { CommandGroup, CommandItem } from '@/components/v2/ui/command'
import Icon from '@/components/v2/ui/icon'
import getSidebarSections from '@/lib/sidebar/getSidebarSections'
import { useSession } from '@/contexts/SessionProvider'
import { useTexts } from '@/contexts/LanguageContext'
import { toSnakeCase } from '@/lib/utils'
import getMonitoringVisibility from '@/lib/sidebar/getMonitoringVisibility'
import { getSpotlightNavigationItems } from './getSpotlightNavigationItems'

interface ISidebarNavigationSearchProps {
  onSelect?: () => void
}

export function SidebarNavigationSearch({ onSelect }: ISidebarNavigationSearchProps) {
  const router = useRouter()
  const pathname = usePathname()
  const { user } = useSession()
  const { TENANTS, SIDEBAR } = useTexts()
  const org = user.simulatedOrganization ?? user.organization
  const hasTrustCenter = org.hasTrustCenter === true
  const trustCenterPath = hasTrustCenter ? `/trust-center/${toSnakeCase(org.name)}` : undefined
  const groups = getSidebarSections(SIDEBAR, {
    hasTrustCenter,
    trustCenterPath,
    ...getMonitoringVisibility(org),
  })
  const navItems = getSpotlightNavigationItems(groups, user)

  if (navItems.length === 0) {
    return null
  }

  const handleNavigate = (url: string) => {
    onSelect?.()
    router.push(url)
  }

  return (
    <CommandGroup heading={TENANTS.COMMAND_SEARCH.NAVIGATION}>
      {navItems.map((item) => {
        const isActive = pathname === item.url || pathname.startsWith(item.url + '/')
        return (
          <CommandItem
            key={`${item.url}-${item.title}`}
            value={item.path}
            onSelect={() => {
              handleNavigate(item.url)
            }}
            className="gap-2 cursor-pointer"
          >
            <Icon icon="arrow-right" className="size-4 text-muted-foreground shrink-0" />
            <span className="flex-1 flex items-center gap-1 min-w-0 truncate">
              {item.path.split('>').map((segment, i) => (
                <span key={i} className="contents">
                  {i > 0 && <Icon icon="chevron-right" className="size-3.5 text-muted-foreground shrink-0" />}
                  <span>{segment}</span>
                </span>
              ))}
            </span>
            {isActive && <Icon icon="check" className="size-4 text-primary shrink-0" />}
          </CommandItem>
        )
      })}
    </CommandGroup>
  )
}
